import * as React from 'react'
import { bool, func, object, string } from 'prop-types'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import StarIcon from '@mui/icons-material/StarOutlined'
import {
  useTheme,
  useMediaQuery,
  styled,
  Typography,
  Chip,
  Button,
  Stack,
  Grid,
  CardActions,
  CardContent,
  CardHeader,
  Card,
  Box,
} from '@mui/material'
import { addToDataLayer_nextgen } from '@helpers/google-tag-manager'

import markerSvg from '../shared/svgs/markerSvg'
import StoreName from '../store-locator/store-name'
import StoreNameV2 from '../store-locator-v2/store-name'
import StoreRating from '../store-locator/store-rating'

const propOptions = { shouldForwardProp: prop => prop !== 'isMobile' }
const StyledCard = styled(
  Card,
  propOptions,
)(({ isMobile }) => ({
  display: 'flex',
  flexDirection: 'row',
  border: '1px solid rgba(0, 0, 0, 0.12)',
  borderRadius: '2px',
  '& > div': {
    '&:first-of-type': {
      paddingRight: 0,
    },
    ...(isMobile && {
      padding: '.5rem .2rem',
    }),
  },
}))

const StyledCardHeader = styled(
  CardHeader,
  propOptions,
)(({ isMobile }) => ({
  padding: '0',
  '.MuiCardHeader-title': {
    display: 'flex',
    flowDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    // ...(isMobile && { flexDirection: 'column !important' }),
    a: {
      display: 'block',
      padding: 0,
      fontWeight: 'bold',
      textTransform: ' uppercase',
      textDecoration: 'none',
      outline: 'none',
    },
    '& > div': {
      fontSize: '1rem',
      // ...(isMobile && { marginLeft: 'unset' }),
    },
  },
}))

const StyledStoreContent = styled(CardContent)({
  width: '100%',
})

const StyledCardActions = styled(CardActions)(({ theme }) => ({
  padding: 0,
  button: {
    textTransform: 'capitalize',
    width: '100%',
    marginTop: '1rem',
    [theme.breakpoints.up('sm')]: {
      width: '50%',
      margin: '0 auto',
    },
    [theme.breakpoints.up('md')]: {
      width: '100%',
    },
  },
}))

const StyledChip = styled(Chip)({
  height: '24px',
})

const StyledAddressContainer = styled(
  Box,
  propOptions,
)(({ isMobile }) => ({
  p: {
    margin: '0',
    ...(isMobile && { maxWidth: '200px' }),
  },
}))

const StyledAddress = styled(Typography)({
  textTransform: 'capitalize',
  whiteSpace: 'pre-line',
  outline: 'none',
})

const StyledDistance = styled(
  Typography,
  propOptions,
)(({ theme, isMobile }) => ({
  textTransform: 'lowercase',
  backgroundColor: theme.palette.grey[100],
  borderRadius: '.2rem',
  padding: '0 .4rem',
  ...(!isMobile && { marginLeft: '1.5rem' }),
}))

const StyledMarker = styled(markerSvg)(({ theme, selected }) => ({
  fill: selected ? theme.palette.error.main : '#0050b5',
  strokeWeight: 2,
  strokeColor: 'white',
}))

const StyledSuccessButton = styled(Button)(({ theme }) => ({
  color: theme.palette.success.main,
  borderColor: theme.palette.success.main,
  svg: {
    marginRight: '.5rem',
  },
}))

const StyledRatingContainer = styled(Box)(({ theme }) => ({
  fontSize: '16px',
  fontWeight: '600',
  color: theme.palette.primary.main,
  display: 'flex',
  alignItems: 'center',
  marginLeft: '10px',
}))

const StyledStarIcon = styled(StarIcon)({
  fontSize: '18px!important',
})

const storeTypesChipColors = { Kids: 'error', Patio: 'success', Outlet: 'primary' }

const getTotalRatings = lifetimeRatings => {
  const { count1star, count2Stars, count3Stars, count4Stars, count5Stars } = lifetimeRatings
  const totalRatings =
    Number(count1star) + Number(count2Stars) + Number(count3Stars) + Number(count4Stars) + Number(count5Stars)
  return totalRatings
}

export default function StoreListDetail({
  handleShowroomClick,
  isSelected,
  letter,
  showRatings,
  storeDetails,
  myStore,
  onSetMyStore,
  isDefaultUserStore,
  noStoreNameLink,
}) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { city, distance, hours, name, phone, selectedRatings, state, storeNumber, title, types, zip } = storeDetails
  const getHours = () => {
    const todaysHours = hours?.filter(s => s.day === new Date().toLocaleString('en-us', { weekday: 'long' }))[0]
    const openTime = ((parseFloat(todaysHours?.openTime) + 11) % 12) + 1
    const closeTime = ((parseFloat(todaysHours?.closeTime) + 11) % 12) + 1
    return { todaysHours, openTime, closeTime }
  }
  const renderHeaderContent = () => (
    <>
      {noStoreNameLink ? (
        <StoreNameV2 mark={{ name }} />
      ) : (
        <StoreName mark={{ StoreNumber: Number(storeNumber), name }} />
      )}
      {showRatings && selectedRatings?.overallScore && (
        <StyledRatingContainer>
          ({`${selectedRatings?.overallScore}`}
          <StyledStarIcon />)
        </StyledRatingContainer>
        // <StoreRating ratingValue={selectedRatings?.overallScore} totalRatings={getTotalRatings(selectedRatings)} />
      )}
    </>
  )

  const onSetDefaultStore = e => {
    e.preventDefault()
    e.stopPropagation()
    addToDataLayer_nextgen('select_your_store', {
      your_store: name,
    })
    onSetMyStore(myStore.storeNumber)
  }

  return (
    <StyledCard sx={{ minWidth: 250 }} isMobile={isMobile} elevation={0}>
      <CardContent sx={{ paddingLeft: '8px' }}>
        <StyledMarker selected={isSelected}>{letter || ''}</StyledMarker>
      </CardContent>
      <StyledStoreContent sx={{ paddingLeft: '0' }}>
        <StyledCardHeader
          isMobile={isMobile}
          title={renderHeaderContent()}
          subheader={
            types?.filter(type => +type?.storeNumber !== +storeNumber).length > 0 && (
              <Stack direction="row" spacing={1}>
                {/* Only show Also Includes if there is a tag name that doesn't match the store name */}
                {types.some(type => !name.includes(type.name)) && (
                  <Typography variant="caption" color={theme.palette.secondary.lighter}>
                    Also includes:
                  </Typography>
                )}
                {types?.map((storeType, i) => {
                  const chipLabel = storeType?.name ?? storeType
                  if (!chipLabel || name.includes(chipLabel)) return null
                  const chipKey = `typeChip_${letter}${i}`
                  const chipColor = storeTypesChipColors[chipLabel] ?? 'warning'
                  return <StyledChip key={chipKey} label={chipLabel} color={chipColor} />
                })}
              </Stack>
            )
          }
        />
        <Grid container direction="row" justifyContent="space-between" alignItems="center">
          <StyledAddressContainer isMobile={isMobile}>
            <StyledAddress variant="body1">
              {title?.toLowerCase()}
              {`\n${city}, ${state} ${zip}`}
            </StyledAddress>
            {!myStore && (
              <Typography variant="body1">
                <a href={`tel:${phone}`}>{phone}</a>
              </Typography>
            )}
            <Typography variant="body1" fontSize={14}>
              {getHours().openTime
                ? `Open today ${getHours().openTime}am to ${getHours().closeTime}pm`
                : `Closed Today`}
            </Typography>
          </StyledAddressContainer>
          {!myStore && !!distance && (
            <StyledDistance variant="overline" isMobile={isMobile}>{`${distance} mi`}</StyledDistance>
          )}
        </Grid>
        <StyledCardActions>
          {!myStore && (
            <Button
              onClick={handleShowroomClick}
              variant="text"
              aria-label="Schedule a showroom visit"
              sx={{ justifyContent: 'flex-start' }}
            >
              Schedule a showroom visit
            </Button>
          )}
          {/* DISABLED: FD-4670 */}
          {/* {myStore && (
            <>
              {isDefaultUserStore ? (
                <StyledSuccessButton variant="outlined" fontWeight={600}>
                  <CheckCircleOutlineIcon />
                  STORE SELECTED
                </StyledSuccessButton>
              ) : (
                <Button variant="outlined" fontWeight={600} onClick={onSetDefaultStore}>
                  SELECT STORE
                </Button>
              )}
            </>
          )} */}
        </StyledCardActions>
      </StyledStoreContent>
    </StyledCard>
  )
}

StoreListDetail.propTypes = {
  handleShowroomClick: func,
  onSetMyStore: func,
  isSelected: bool,
  letter: string,
  showRatings: bool,
  storeDetails: object,
  myStore: bool,
  isDefaultUserStore: bool,
  noStoreNameLink: bool,
}
